import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  HiOutlineLockClosed,
  HiOutlineUser,
  HiOutlineArrowRight,
  HiOutlineShieldCheck,
  HiOutlineLightningBolt,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineEyeOff,
} from "react-icons/hi";
import Labels from "../../services/apiLabels";
import { POST } from "../../constant/apiMethods";
import { LOGIN, TOASTER_SHOW, toaster_e } from "../../constant";
import { DEFAULT_ROUTE } from "../../router";
import OverLayLoader from "../../components/OverLayLoader";
import GoogleAuthButton from "../../components/GoogleAuthButton";
import { USER_TYPE_CLIENT, isDealerUser, redirectAfterLogin } from "../../utils/userTypes";
import "./client-login.css";

const publicUrl = process.env.PUBLIC_URL || "";
const VISUAL_IMAGE = `${publicUrl}/images/hero-evaluation.jpg`;

function validateLoginFields(username, password) {
  const errors = {};
  if (!String(username || "").trim()) {
    errors.username = "Please enter your user name.";
  }
  if (!password) {
    errors.password = "Please enter your password.";
  }
  return errors;
}

function Login(props) {
  const { login, googleLogin, auth, toasterShow } = props;
  const history = useHistory();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const valuationRequired = Boolean(location.state?.valuationRequired);
  const [sessionExpired] = useState(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("expired") === "1";
  });

  const fieldErrors = useMemo(
    () => validateLoginFields(username, password),
    [username, password]
  );
  const canSubmit = Object.keys(fieldErrors).length === 0;

  useEffect(() => {
    if (!sessionExpired) return;
    toasterShow({
      show: true,
      autoHide: true,
      type: "error",
      heading: "Not authorized",
      description: "Your token is not authorized. Please log in again.",
    });
    history.replace({ pathname: "/login", state: location.state });
  }, [sessionExpired, toasterShow, history, location.state]);

  const onLoginSuccessHandler = (response) => {
    const payload = response?.data || response || auth;
    const from = location.state?.from;
    if (isDealerUser(payload) || isDealerUser(payload?.user || {})) {
      history.replace(redirectAfterLogin(payload, from));
      return;
    }
    if (from?.pathname) {
      // Keep the query string, otherwise deep links such as ?id=12 are lost.
      history.replace({
        pathname: from.pathname,
        search: from.search || "",
        hash: from.hash || "",
      });
      return;
    }
    history.replace(valuationRequired ? "/" : DEFAULT_ROUTE);
  };

  const markTouched = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setErrors(validateLoginFields(username, password));
  };

  const onUsernameChange = (value) => {
    setUsername(value);
    if (touched.username || touched.password) {
      setErrors(validateLoginFields(value, password));
    }
  };

  const onPasswordChange = (value) => {
    setPassword(value);
    if (touched.username || touched.password) {
      setErrors(validateLoginFields(username, value));
    }
  };

  const loginHandler = (event) => {
    event.preventDefault();
    const nextErrors = validateLoginFields(username, password);
    setTouched({ username: true, password: true });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    login(
      {
        username: username.trim(),
        password,
      },
      onLoginSuccessHandler
    );
  };

  const onGoogleCredential = (credential, error) => {
    if (error || !credential) {
      toasterShow({
        show: true,
        autoHide: true,
        type: toaster_e,
        heading: "Google sign-in",
        description:
          error?.message || "Google sign-in was cancelled or failed.",
      });
      return;
    }
    googleLogin(
      {
        id_token: credential,
        type: USER_TYPE_CLIENT,
      },
      onLoginSuccessHandler
    );
  };

  if (auth.token) {
    return <OverLayLoader alwaysShow={true} />;
  }

  const showUsernameError = touched.username ? errors.username : "";
  const showPasswordError = touched.password ? errors.password : "";

  return (
    <div className="pi-login">
      <div className="pi-login__fx" aria-hidden="true">
        <span className="pi-login__glow pi-login__glow--a" />
        <span className="pi-login__glow pi-login__glow--b" />
        <span className="pi-login__grid" />
      </div>

      <div className="pi-login__inner">
        <div className="pi-login__shell">
          <section className="pi-login__panel pi-login__panel--form">
            <header className="pi-login__head">
              <p className="pi-label">Welcome back</p>
              <h1>Log in</h1>
              <p className="pi-login__lead">
                {valuationRequired
                  ? "Sign in to continue your vehicle valuation."
                  : "Access your appointments, credits, and referral rewards. Dealer accounts can also sign in here."}
              </p>
            </header>

            {valuationRequired ? (
              <div className="pi-login__alert" role="status">
                Please log in or sign up to get your vehicle valuation.
              </div>
            ) : null}
            {sessionExpired ? (
              <div className="pi-login__alert" role="alert">
                Your token is not authorized. Please log in again.
              </div>
            ) : null}

            <form
              className="pi-login__form"
              onSubmit={loginHandler}
              autoComplete="on"
              noValidate
            >
              <label
                className={`pi-login__field${
                  showUsernameError ? " is-invalid" : ""
                }`}
              >
                <span className="pi-login__field-label">
                  User name
                  <span className="pi-login__req" aria-hidden="true">
                    *
                  </span>
                </span>
                <span className="pi-login__control">
                  <HiOutlineUser size={18} aria-hidden />
                  <input
                    type="text"
                    name="username"
                    autoComplete="username"
                    placeholder="Enter user name"
                    value={username}
                    aria-required="true"
                    aria-invalid={Boolean(showUsernameError)}
                    aria-describedby={
                      showUsernameError ? "login-username-error" : undefined
                    }
                    onChange={(e) => onUsernameChange(e.target.value)}
                    onBlur={() => markTouched("username")}
                  />
                </span>
                {showUsernameError ? (
                  <p
                    id="login-username-error"
                    className="pi-login__field-error"
                    role="alert"
                  >
                    {showUsernameError}
                  </p>
                ) : null}
              </label>

              <div
                className={`pi-login__field${
                  showPasswordError ? " is-invalid" : ""
                }`}
              >
                <span className="pi-login__field-label" id="login-password-label">
                  Password
                  <span className="pi-login__req" aria-hidden="true">
                    *
                  </span>
                </span>
                <span className="pi-login__control">
                  <HiOutlineLockClosed size={18} aria-hidden />
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    aria-required="true"
                    aria-invalid={Boolean(showPasswordError)}
                    aria-describedby={
                      showPasswordError ? "login-password-error" : undefined
                    }
                    onChange={(e) => onPasswordChange(e.target.value)}
                    onBlur={() => markTouched("password")}
                    aria-labelledby="login-password-label"
                  />
                  <button
                    type="button"
                    className="pi-login__toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff size={18} aria-hidden />
                    ) : (
                      <HiOutlineEye size={18} aria-hidden />
                    )}
                  </button>
                </span>
                {showPasswordError ? (
                  <p
                    id="login-password-error"
                    className="pi-login__field-error"
                    role="alert"
                  >
                    {showPasswordError}
                  </p>
                ) : null}
              </div>

              <div className="pi-login__meta">
                <label className="pi-login__check">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <Link to="/forgetpassword" className="pi-login__link">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="pi-login__submit"
                disabled={!canSubmit || Boolean(auth.loging)}
              >
                {auth.loging ? "Signing in…" : "Sign in"}
                {!auth.loging ? (
                  <HiOutlineArrowRight size={18} aria-hidden />
                ) : null}
              </button>
            </form>

            <div className="pi-login__or" aria-hidden="true">
              <span>or</span>
            </div>

            <GoogleAuthButton
              text="signin_with"
              disabled={Boolean(auth.loging)}
              onCredential={onGoogleCredential}
            />

            <p className="pi-login__footer-note">
              Don&apos;t have an account?{" "}
              <Link
                to={{
                  pathname: "/signup",
                  state: location.state,
                }}
              >
                Sign up
              </Link>
            </p>
          </section>

          <aside
            className="pi-login__panel pi-login__panel--visual"
            style={{ backgroundImage: `url(${VISUAL_IMAGE})` }}
            aria-hidden="true"
          >
            <span className="pi-login__visual-shade" />
            <div className="pi-login__visual-copy">
              <p className="pi-login__visual-kicker">AutoTrade Port</p>
              <h2>
                Sell smarter.
                <span> Track everything.</span>
              </h2>
              <ul className="pi-login__visual-points">
                <li>
                  <HiOutlineLightningBolt size={16} />
                  Instant valuations and bookings
                </li>
                <li>
                  <HiOutlineClock size={16} />
                  Follow appointments in one place
                </li>
                <li>
                  <HiOutlineShieldCheck size={16} />
                  Secure account and referral credits
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (data, callBack) =>
    dispatch({
      type: LOGIN,
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.login,
        data,
        callBack,
      },
    }),
  googleLogin: (data, callBack) =>
    dispatch({
      type: LOGIN,
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.googleAuth,
        hideOverlayLoader: true,
        data,
        callBack,
      },
    }),
  toasterShow: (data) =>
    dispatch({
      type: TOASTER_SHOW,
      payload: {
        ...data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
